import { ListMusic, Mic, Music, Sparkles, User, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import Pricing from './components/Pricing';
import PrivacyPolicy from './components/PrivacyPolicy';
import PublicOffer from './components/PublicOffer';
import Requisites from './components/Requisites';
import UserAgreement from './components/UserAgreement';

import type { Tariff } from '../api/client';
import {
  ApiError,
  client,
  VISIBLE_SONG_STATUSES,
  type Song,
} from '../api/client';
import { getCachedTariffs, refreshTariffs } from '../api/tariffsCache';
import { formatGeneratedLyrics } from './utils/formatters';

import { ITEMS_PER_PAGE } from './lib/constants.js';
import { translateApiError } from './lib/errors';
import { trackPaymentSuccess } from './lib/metrika';
import type { ActiveModal, ActiveTab } from './types/domain';

import { useGenerationForm } from './hooks/useGenerationForm';
import { useModal } from './hooks/useModal';
import { useProfile } from './hooks/useProfile.js';
import { useSongs } from './hooks/useSongs';

import { AnonymousRecovery } from './components/features/auth/AnonymousRecovery';
import { ProfileAuth } from './components/features/auth/ProfileAuth';
import { StepGenerating } from './components/features/generation/StepGenerating';
import { StepStart } from './components/features/generation/StepStart';
import { StepStyle } from './components/features/generation/StepStyle';
import { StepText } from './components/features/generation/StepText';
import { StepVocal } from './components/features/generation/StepVocal';
import { StepVoiceUpload } from './components/features/generation/StepVoiceUpload';
import { ProfileInfo } from './components/features/profile/ProfileInfo';
import { TariffCards } from './components/features/profile/TariffCards';
import { TrackCard } from './components/features/songs/TrackCard';
import { AnonymousRecoveryModal } from './components/ui/AnonymousRecoveryModal';
import { InfoModal } from './components/ui/InfoModal';

// ─── Step progress indicator ────────────────────────────────────────────────

const STEP_LABELS = ['Текст', 'Стиль', 'Вокал', 'Результат'] as const;

/** Persists the song currently being generated so a page refresh can resume watching it. */
const ACTIVE_GENERATION_KEY = 'active_generation_song_id';

function stepNumber(step: string): number {
  return (
    { start: 0, text: 1, style: 2, vocal: 3, generating: 3, result: 4 }[step] ??
    0
  );
}

// ─── Nav button helper ──────────────────────────────────────────────────────

/** 1 песня, 2 песни, 5 песен, 11 песен, 21 песня... */
function pluralizeSongs(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} песня`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${n} песни`;
  }
  return `${n} песен`;
}

interface NavButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  /** Число в кружке-уведомлении. Не показывается, если 0, undefined или не число. */
  badge?: number;
}

function NavButton({ active, onClick, icon, label, badge }: NavButtonProps) {
  const count =
    typeof badge === 'number' && Number.isFinite(badge) && badge > 0
      ? Math.floor(badge)
      : null;

  return (
    <button
      onClick={onClick}
      aria-label={count !== null ? `${label}, ${pluralizeSongs(count)}` : label}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-xl text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all ${
        active
          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
          : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-400 hover:text-purple-600'
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {count !== null && (
        <span
          aria-hidden="true"
          className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1.5 text-xs font-bold leading-none tabular-nums ${
            active
              ? 'bg-white text-[#364153]'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
          }`}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

// ─── Legal modal content ────────────────────────────────────────────────────

const LEGAL_MODAL_TITLES: Record<string, string> = {
  privacy: 'Политика конфиденциальности',
  terms: 'Пользовательское соглашение',
  offer: 'Публичная оферта',
  requisites: 'Реквизиты',
  pricing: 'Цены',
};
const LEGAL_MODALS = new Set(Object.keys(LEGAL_MODAL_TITLES));

const FOOTER_LEGAL_KEYS: ActiveModal[] = [
  'privacy',
  'terms',
  'offer',
  'requisites',
  'pricing',
];

function LegalModalContent({ modal }: { modal: string }) {
  if (modal === 'privacy') return <PrivacyPolicy />;
  if (modal === 'terms') return <UserAgreement />;
  if (modal === 'offer') return <PublicOffer />;
  if (modal === 'requisites') return <Requisites />;
  if (modal === 'pricing') return <Pricing />;
  return null;
}

// ─── App ────────────────────────────────────────────────────────────────────

const HOME_TARIFF_PARAMS = {
  order: 'ASC' as const,
  field: 'price' as const,
  limit: 50,
  offset: 0,
};

function getCheapestTariff(
  tariffs: Tariff[],
  itemTypes: readonly string[],
): Tariff | null {
  return (
    tariffs
      .filter((tariff) =>
        tariff.items.some((item) => itemTypes.includes(item.type)),
      )
      .filter(
        (tariff) =>
          Number.isFinite(Number(tariff.price.amount)) &&
          Number(tariff.price.amount) >= 0,
      )
      .sort((a, b) => Number(a.price.amount) - Number(b.price.amount))[0] ??
    null
  );
}

export default function App() {
  // ── State ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('generation');
  const [tariffs, setTariffs] = useState<Tariff[]>(
    () => getCachedTariffs(HOME_TARIFF_PARAMS)?.tariffs ?? [],
  );
  // Tariffs are refreshed during bootstrap, after the anonymous session exists.
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [generationMode, setGenerationMode] = useState<'model' | 'voice'>(
    'model',
  );
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  // Recovery codes are bearer secrets: keep them only in component memory,
  // never in localStorage/sessionStorage.
  const [anonymousRecoveryCode, setAnonymousRecoveryCode] = useState<
    string | null
  >(null);
  const [anonymousRecoveryUserId, setAnonymousRecoveryUserId] = useState<
    string | null
  >(null);
  const pendingPaymentRef = useRef<(() => Promise<void>) | null>(null);

  const {
    profile,
    load: loadProfile,
    reset: resetProfile,
    patch: patchProfile,
  } = useProfile();
  const { songs, setSongs, currentSong, setCurrentSong, loadAndSync } =
    useSongs();
  const {
    activeModal,
    setActiveModal,
    errorMessage,
    openError,
    openPaymentError,
    close: closeModal,
  } = useModal();
  const {
    form,
    patch: patchForm,
    reset: resetForm,
    goBack,
    toggleTheme,
    effectiveStyle,
  } = useGenerationForm();

  const generationWatchRef = useRef<{ stop: () => void } | null>(null);
  const voiceWatchRef = useRef<{ stop: () => void } | null>(null);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const stopWatch = () => {
    generationWatchRef.current?.stop();
    generationWatchRef.current = null;
  };

  const reloadUserData = useCallback(async () => {
    await Promise.all([loadProfile(), loadAndSync()]);
  }, [loadProfile, loadAndSync]);

  const ensureAnonymousRecoveryBeforePayment = useCallback(
    async (continuePayment: () => Promise<void>) => {
      if (!profile.isAnonymous) {
        await continuePayment();
        return;
      }

      // If the code was already issued in this page session, show the same
      // in-memory value again instead of requesting another one-shot code.
      if (anonymousRecoveryCode && anonymousRecoveryUserId === profile.userId) {
        pendingPaymentRef.current = continuePayment;
        setActiveModal('anonymousRecovery');
        return;
      }

      try {
        const response = await client.account.issueRecoveryCode();
        pendingPaymentRef.current = continuePayment;
        setAnonymousRecoveryUserId(profile.userId);
        setAnonymousRecoveryCode(response.code);
        setActiveModal('anonymousRecovery');
      } catch (error) {
        // The endpoint is intentionally one-shot. A 409 means the server already
        // issued the code, so payment may continue even if this browser no longer
        // has the locally cached copy.
        if (
          error instanceof ApiError &&
          error.status === 409 &&
          error.body.message === 'AnonymousRecoveryCodeAlreadyExistsError'
        ) {
          await continuePayment();
          return;
        }
        throw error;
      }
    },
    [
      profile.isAnonymous,
      profile.userId,
      anonymousRecoveryCode,
      anonymousRecoveryUserId,
      setActiveModal,
    ],
  );

  const continueAfterAnonymousRecovery = useCallback(async () => {
    const payment = pendingPaymentRef.current;
    pendingPaymentRef.current = null;
    setAnonymousRecoveryCode(null);
    setAnonymousRecoveryUserId(null);
    closeModal();
    if (payment) await payment();
  }, [closeModal]);

  const closeAnonymousRecovery = useCallback(() => {
    // Keep the code in memory so the user can reopen and copy it before
    // leaving the page; it is discarded on successful continuation or reload.
    pendingPaymentRef.current = null;
    closeModal();
  }, [closeModal]);

  const purchaseTariffForItem = useCallback(
    async (itemTypes: readonly string[]) => {
      try {
        let availableTariffs = tariffs;
        let tariff = getCheapestTariff(availableTariffs, itemTypes);
        // Cache-first: only make an additional request when the cached list
        // does not contain a tariff for the requested generation type.
        if (!tariff) {
          const refreshed = await refreshTariffs(HOME_TARIFF_PARAMS);
          availableTariffs = refreshed.tariffs;
          setTariffs(availableTariffs);
          tariff = getCheapestTariff(availableTariffs, itemTypes);
        }
        if (!tariff) {
          openPaymentError(
            'Не удалось найти тариф для этой генерации. Попробуйте позже.',
          );
          return;
        }

        await ensureAnonymousRecoveryBeforePayment(async () => {
          const response = await client.payments.purchaseGeneration({
            tariffId: tariff!.id,
            context: { source: 'ROOT' },
          });
          window.location.href = response.paymentUrl;
        });
      } catch (error) {
        openPaymentError(translateApiError(error));
      }
    },
    [tariffs, ensureAnonymousRecoveryBeforePayment, openPaymentError],
  );

  const goToTab = (tab: ActiveTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  /**
   * Called once on load when the URL contains `?payment={payment_id}`
   * (the page the backend redirects to after the payment provider).
   * Fetches the payment, reopens the tab the user started the purchase
   * from, and surfaces a modal for failed/confirmed payments.
   */
  const handleReturnFromPayment = useCallback(
    async (paymentId: string) => {
      try {
        const payment = await client.payments.get(paymentId);
        console.info(
          '[payment] returned from provider:',
          payment.id,
          payment.status,
        );

        switch (payment.context?.source) {
          case 'MY_SONGS':
            goToTab('songs');
            break;
          case 'ROOT':
            goToTab('generation');
            break;
          case 'PROFILE':
            goToTab('profile');
            break;
        }

        if (payment.status === 'FAILED') {
          openPaymentError('Платёж не был завершён. Попробуйте ещё раз.');
        } else if (payment.status === 'CONFIRMED') {
          // Idempotent: sent once per payment id (tracked in localStorage).
          // Placed before the awaits so a failing reload can't lose the goal.
          trackPaymentSuccess(payment);
          await reloadUserData();
          setActiveModal('paymentSuccess');
        }
        // NEW / PENDING / CANCELLED: nothing to show yet, the balance
        // will simply update on the next reloadUserData().
      } catch (error) {
        openError(translateApiError(error));
      }
    },
    [openError, openPaymentError, reloadUserData],
  );

  useEffect(() => {
    const handleUnauthorized = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('anonymous_token');
      localStorage.removeItem(ACTIVE_GENERATION_KEY);

      resetProfile();
      goToTab('profile');
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () =>
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [resetProfile]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!localStorage.getItem('anonymous_token')) {
        try {
          const res = await client.account.registerAnonymous();
          localStorage.setItem('anonymous_token', res.token);
        } catch (e) {
          console.error('Failed to create anonymous session', e);
          setIsAppLoading(false);
          return;
        }
      }

      // Do not start authenticated requests until the anonymous token has
      // been registered and persisted in localStorage.
      const tariffsPromise = refreshTariffs(HOME_TARIFF_PARAMS)
        .then((response) => setTariffs(response.tariffs))
        .catch((error) => {
          // Keep cached tariffs usable if the backend is temporarily unavailable.
          console.error('Failed to refresh tariffs:', error);
        });

      try {
        await Promise.all([reloadUserData(), tariffsPromise]);
        const params = new URLSearchParams(window.location.search);
        const paymentId = params.get('payment');

        if (paymentId) {
          // Always strip the query param so a refresh doesn't re-trigger this.
          window.history.replaceState(null, '', window.location.pathname);
          await handleReturnFromPayment(paymentId);
        }
      } catch (error) {
        console.error('Bootstrap error:', error);

        openError(translateApiError(error));
      } finally {
        setIsAppLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!profile.isLoggedIn || activeTab !== 'songs') return;
    loadAndSync().catch((err) => openError(translateApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, profile.isLoggedIn]);

  useEffect(
    () => () => {
      stopWatch();
      voiceWatchRef.current?.stop();
    },
    [],
  );

  /**
   * Starts (or resumes) watching a song's generation. Shared by
   * startSongGeneration() and the resume-on-reload effect below so both
   * paths handle finish/failure identically.
   */
  const watchGeneration = useCallback(
    (songId: string) => {
      stopWatch();
      const abortController = new AbortController();

      const handle = client.songs.watchGeneration(
        songId,
        {
          onQueued: () => patchForm({ phase: 'queued', eta: null }),
          onAwaiting: (eta) => patchForm({ phase: 'awaiting_generation', eta }),
          onGenerating: () => patchForm({ phase: 'generating', eta: null }),
          onFinished: async (_tracks, generationsBalance) => {
            localStorage.removeItem(ACTIVE_GENERATION_KEY);
            patchProfile({ generationsBalance });
            try {
              const songsResponse = await client.songs.list({
                order: 'DESC',
                field: 'created_at',
                statuses: VISIBLE_SONG_STATUSES,
              });
              setSongs(songsResponse.songs);
              const generated =
                songsResponse.songs.find((s) => s.id === songId) ??
                songsResponse.songs[0] ??
                null;
              setCurrentSong(generated);
            } finally {
              patchForm({ step: 'result', phase: null, eta: null });
            }
          },
          onFailed: async () => {
            localStorage.removeItem(ACTIVE_GENERATION_KEY);
            patchForm({ step: 'start', phase: null, eta: null });
            setActiveModal('generationFailed');
            await reloadUserData().catch(() => undefined);
          },
        },
        abortController.signal,
      );

      generationWatchRef.current = {
        stop: () => {
          handle.stop();
          abortController.abort();
        },
      };
    },
    [
      patchForm,
      patchProfile,
      setSongs,
      setCurrentSong,
      reloadUserData,
      setActiveModal,
    ],
  );

  // Resume a generation that was still running when the page was refreshed.
  useEffect(() => {
    if (isAppLoading) return;
    const songId = localStorage.getItem(ACTIVE_GENERATION_KEY);
    if (!songId) return;

    let cancelled = false;
    (async () => {
      try {
        const event = await client.songs.getGeneration(songId);
        if (cancelled) return;

        if (event.type === 'finished') {
          localStorage.removeItem(ACTIVE_GENERATION_KEY);
          patchProfile({ generationsBalance: event.generationsBalance });
          const songsResponse = await client.songs.list({
            order: 'DESC',
            field: 'created_at',
            statuses: VISIBLE_SONG_STATUSES,
          });
          setSongs(songsResponse.songs);
          const generated =
            songsResponse.songs.find((s) => s.id === songId) ??
            songsResponse.songs[0] ??
            null;
          setCurrentSong(generated);
          goToTab('generation');
          patchForm({ step: 'result' });
          return;
        }

        if (event.type === 'failed') {
          localStorage.removeItem(ACTIVE_GENERATION_KEY);
          setActiveModal('generationFailed');
          await reloadUserData().catch(() => undefined);
          return;
        }

        goToTab('generation');
        patchForm({
          step: 'generating',
          phase:
            event.type === 'awaiting_generation'
              ? 'awaiting_generation'
              : event.type === 'generating'
                ? 'generating'
                : 'queued',
          eta: event.type === 'awaiting_generation' ? event.eta : null,
        });
        watchGeneration(songId);
      } catch {
        localStorage.removeItem(ACTIVE_GENERATION_KEY);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppLoading]);

  const generateText = async () => {
    if (!profile.isLoggedIn) {
      goToTab('profile');
      return;
    }
    try {
      setIsActionLoading(true);

      let reqDescription = null;
      if (form.theme && form.songDescription) {
        reqDescription = `Theme: ${form.theme}\nDescription: ${form.songDescription}`;
      } else if (form.theme) {
        reqDescription = `Theme: ${form.theme}`;
      } else if (form.songDescription) {
        reqDescription = `Description: ${form.songDescription}`;
      }

      const response = await client.songs.generateLyrics({
        title: form.songName,
        description: reqDescription,
      });
      patchForm({ songText: formatGeneratedLyrics(response.lyrics) });
    } catch (error) {
      openError(translateApiError(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  const placeSong = async (
    vocal:
      | { type: 'MODEL'; gender: typeof form.gender }
      | { type: 'HUMAN'; voiceId: string },
  ) => {
    const style = effectiveStyle;
    const { songId } = await client.songs.placeSong({
      preset: {
        title: form.songName,
        description: form.songDescription || null,
        theme: form.theme || null,
        lyrics: form.songText,
        style,
        vocal,
      },
    });
    localStorage.setItem(ACTIVE_GENERATION_KEY, songId);
    patchForm({ step: 'generating', phase: 'queued', eta: null });
    watchGeneration(songId);
  };

  const startSongGeneration = async () => {
    if (!profile.isLoggedIn) {
      goToTab('profile');
      return;
    }
    const style = effectiveStyle;
    if (!form.songName || !form.songText || !style) {
      openError('Заполните название, текст и стиль песни');
      return;
    }
    try {
      setIsActionLoading(true);
      if (generationMode === 'voice') {
        if (!voiceFile) {
          openError('Сначала загрузите и проверьте файл с голосом');
          return;
        }
        patchForm({ step: 'generating', phase: 'preparing_voice', eta: null });
        const { voiceId } = await client.voices.create(voiceFile);
        await client.voices.prepare(voiceId);
        const abort = new AbortController();
        voiceWatchRef.current?.stop();
        voiceWatchRef.current = client.voices.watchGeneration(
          voiceId,
          {
            onQueued: () => patchForm({ phase: 'preparing_voice', eta: null }),
            onPreparing: () =>
              patchForm({ phase: 'preparing_voice', eta: null }),
            onReady: () => {
              void placeSong({ type: 'HUMAN', voiceId }).catch((error) => {
                patchForm({ step: 'vocal' });
                openError(translateApiError(error));
              });
            },
            onFailed: () => {
              patchForm({ step: 'vocal' });
              openError(
                'Не удалось подготовить голос. Попробуйте другой файл.',
              );
            },
          },
          abort.signal,
        );
      } else {
        patchForm({ step: 'generating', phase: 'queued', eta: null });
        await placeSong({ type: 'MODEL', gender: form.gender });
      }
    } catch (error) {
      openError(
        translateApiError(error, {
          title: form.songName,
          style,
          gender: form.gender,
        }),
      );
      patchForm({ step: 'vocal', phase: null, eta: null });
    } finally {
      setIsActionLoading(false);
    }
  };

  const regenerateSong = useCallback(
    async (songId: string) => {
      try {
        stopWatch();
        const response = await client.songs.placeAdditionalGeneration(songId);
        localStorage.setItem(ACTIVE_GENERATION_KEY, response.songId);

        // The wizard form is empty (or holds another song's data) when an
        // additional generation starts from a saved song, so take the title
        // from the song itself.
        const title =
          songs.find((s) => s.id === songId)?.preset.title ??
          (currentSong?.id === songId ? currentSong.preset.title : '');

        setCurrentSong(null);
        goToTab('generation');
        patchForm({
          step: 'generating',
          phase: 'queued',
          eta: null,
          songName: title,
        });
        watchGeneration(response.songId);
      } catch (error) {
        openError(translateApiError(error));
        throw error;
      }
    },
    [openError, patchForm, setCurrentSong, watchGeneration, songs, currentSong],
  );

  // ── Loaders ───────────────────────────────────────────────────────────────

  if (isAppLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 text-gray-900 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-xl text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-spin" />
          <p className="text-gray-700 font-medium">Загружаем приложение...</p>
        </div>
      </div>
    );
  }

  // ── Pagination ────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(songs.length / ITEMS_PER_PAGE);
  const pagedSongs = songs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  const currentStep = form.step;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 text-gray-900 flex flex-col">
      {/* ── Header nav ── */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-2 sm:px-4">
          <nav className="flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4">
            <NavButton
              active={activeTab === 'generation'}
              onClick={() => {
                goToTab('generation');
                if (form.step !== 'generating') patchForm({ step: 'start' });
              }}
              icon={<Music className="w-5 h-5" />}
              label="Генерация"
            />
            <NavButton
              active={activeTab === 'songs'}
              onClick={() => goToTab('songs')}
              icon={<ListMusic className="w-5 h-5" />}
              label="Мои песни"
              badge={profile.savedSongsCount}
            />
            <NavButton
              active={activeTab === 'profile'}
              onClick={() => goToTab('profile')}
              icon={<User className="w-5 h-5" />}
              label="Личный кабинет"
            />
          </nav>
        </div>
      </header>

      {/* ── Step progress bar ── */}
      {activeTab === 'generation' && currentStep !== 'start' && (
        <div className="bg-gradient-to-r from-purple-100 via-pink-100 to-blue-100 py-2 sm:py-4 px-2 sm:px-4 border-b-2 border-purple-200">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              {STEP_LABELS.map((defaultLabel, idx) => {
                const label =
                  idx === 2 && generationMode === 'voice'
                    ? 'Загрузка голоса'
                    : defaultLabel;
                const n = stepNumber(currentStep);
                return (
                  <div key={label} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full text-white text-xs sm:text-sm font-semibold ${
                        n > idx + 1
                          ? 'bg-green-500'
                          : n === idx + 1
                            ? 'bg-purple-600'
                            : 'bg-gray-300'
                      }`}
                    >
                      {idx + 1}
                    </div>
                    <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs hidden sm:inline text-gray-700 font-medium">
                      {label}
                    </span>
                    {idx < 3 && (
                      <div className="w-4 sm:w-8 h-0.5 bg-gray-300 mx-1 sm:mx-2 hidden sm:block" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 flex-1 flex items-center justify-center">
        {/* ── Generation tab ── */}
        {activeTab === 'generation' && (
          <AnimatePresence mode="wait">
            {currentStep === 'start' && (
              <StepStart
                key="start"
                agreed={agreedToTerms}
                onAgreeChange={setAgreedToTerms}
                disabled={isActionLoading}
                voiceSongHasBalance={profile.vocalGenerationsBalance > 0}
                createSongHasBalance={profile.generationsBalance > 0}
                onVoiceSong={() => {
                  setGenerationMode('voice');
                  if (profile.vocalGenerationsBalance > 0) {
                    patchForm({ step: 'text' });
                  } else {
                    void purchaseTariffForItem([
                      'GENERATION_VOICE',
                      'GENERATION_VOCAL',
                    ]);
                  }
                }}
                onCreateSong={() => {
                  setGenerationMode('model');
                  if (profile.generationsBalance > 0) {
                    patchForm({ step: 'text' });
                  } else {
                    void purchaseTariffForItem(['GENERATION']);
                  }
                }}
                onOpenPrivacy={() => setActiveModal('privacy')}
                onOpenTerms={() => setActiveModal('terms')}
              />
            )}

            {currentStep === 'text' && (
              <StepText
                songName={form.songName}
                songText={form.songText}
                songDescription={form.songDescription}
                theme={form.theme}
                isLoading={isActionLoading}
                onNameChange={(v) => patchForm({ songName: v })}
                onTextChange={(v) => patchForm({ songText: v })}
                onDescriptionChange={(v) => patchForm({ songDescription: v })}
                onThemeToggle={toggleTheme}
                onGenerateText={generateText}
                onBack={goBack}
                onNext={() => patchForm({ step: 'style' })}
              />
            )}

            {currentStep === 'style' && (
              <StepStyle
                selectedStyle={form.selectedStyle}
                customStyle={form.customStyle}
                onSelectStyle={(name) =>
                  patchForm({ selectedStyle: name, customStyle: '' })
                }
                onCustomStyleChange={(v) =>
                  patchForm({ customStyle: v, selectedStyle: '' })
                }
                onBack={goBack}
                onNext={() => patchForm({ step: 'vocal' })}
              />
            )}

            {currentStep === 'vocal' && generationMode === 'model' && (
              <StepVocal
                gender={form.gender}
                onGenderChange={(g) => patchForm({ gender: g })}
                onBack={goBack}
                onSubmit={startSongGeneration}
              />
            )}

            {currentStep === 'vocal' && generationMode === 'voice' && (
              <StepVoiceUpload
                onBack={goBack}
                onReady={setVoiceFile}
                onSubmit={startSongGeneration}
                submitting={isActionLoading}
              />
            )}

            {currentStep === 'generating' && form.phase && (
              <StepGenerating
                songName={form.songName}
                phase={form.phase}
                eta={form.eta}
              />
            )}

            {currentStep === 'result' && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl w-full"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  🎉 Ваши треки готовы!
                </h2>

                {!currentSong ? (
                  <div className="bg-white/80 backdrop-blur-md rounded-3xl p-12 text-center shadow-xl">
                    <Music className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 text-lg mb-6">
                      Песня не найдена. Откройте раздел «Мои песни».
                    </p>
                    <button
                      onClick={() => goToTab('songs')}
                      className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
                    >
                      Перейти к моим песням
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <TrackCard
                      song={currentSong}
                      playingTrack={playingTrack}
                      setPlayingTrack={setPlayingTrack}
                      tracksBalance={profile.tracksBalance}
                      onReloadData={reloadUserData}
                      onPaymentStarted={() => setIsActionLoading(true)}
                      onPaymentFinished={() => setIsActionLoading(false)}
                      onError={openPaymentError}
                      onBeforePayment={async () => {
                        let allowed = false;
                        try {
                          await ensureAnonymousRecoveryBeforePayment(
                            async () => {
                              allowed = true;
                            },
                          );
                        } catch (error) {
                          openPaymentError(translateApiError(error));
                        }
                        return allowed;
                      }}
                      paymentContextSource="ROOT"
                      showRegenerate
                      onRegenerate={regenerateSong}
                      additionalGenerationBalance={
                        profile.additionalGenerationBalance
                      }
                    />
                    <button
                      onClick={() => {
                        resetForm();
                        setCurrentSong(null);
                      }}
                      className="w-full px-8 py-4 bg-white/80 rounded-xl font-bold text-lg hover:bg-white transition-all text-gray-900 shadow-md"
                    >
                      Создать ещё одну песню
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* ── Songs tab ── */}
        {activeTab === 'songs' && (
          <div className="max-w-5xl w-full">
            <h2 className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Мои песни
            </h2>

            {songs.length === 0 ? (
              <div className="bg-white/80 backdrop-blur-md rounded-3xl p-12 text-center shadow-xl">
                <Music className="w-20 h-20 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg mb-6">
                  У вас пока нет сохраненных песен
                </p>
                <button
                  onClick={() => {
                    goToTab('generation');
                    patchForm({ step: 'start' });
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
                >
                  Создать первую песню
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-6">
                  {pagedSongs.map((song: Song) => (
                    <div
                      key={song.id}
                      className="bg-white/80 backdrop-blur-md rounded-2xl p-6 shadow-xl"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {song.preset.title}
                          </h3>
                          <div className="flex gap-4 text-sm text-gray-600 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Music className="w-4 h-4" />
                              {song.preset.style}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mic className="w-4 h-4" />
                              {song.preset.vocal.type === 'HUMAN'
                                ? 'Свой'
                                : song.preset.vocal.gender === 'male'
                                  ? 'Мужской'
                                  : song.preset.vocal.gender === 'female'
                                    ? 'Женский'
                                    : 'Дуэт М+Ж'}{' '}
                              вокал
                            </span>
                            <span className="text-gray-400">
                              {new Date(song.createdAt).toLocaleDateString(
                                'ru-RU',
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <TrackCard
                          song={song}
                          playingTrack={playingTrack}
                          setPlayingTrack={setPlayingTrack}
                          onReloadData={reloadUserData}
                          tracksBalance={profile.tracksBalance}
                          onPaymentStarted={() => setIsActionLoading(true)}
                          onPaymentFinished={() => setIsActionLoading(false)}
                          onError={openPaymentError}
                          onBeforePayment={async () => {
                            let allowed = false;
                            try {
                              await ensureAnonymousRecoveryBeforePayment(
                                async () => {
                                  allowed = true;
                                },
                              );
                            } catch (error) {
                              openPaymentError(translateApiError(error));
                            }
                            return allowed;
                          }}
                          paymentContextSource="MY_SONGS"
                          showRegenerate
                          onRegenerate={regenerateSong}
                          additionalGenerationBalance={
                            profile.additionalGenerationBalance
                          }
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            goToTab('generation');
                            patchForm({
                              step: 'result',
                              songName: song.preset.title,
                              songText: song.preset.lyrics,
                              songDescription: song.preset.description ?? '',
                              theme: song.preset.theme ?? '',
                              selectedStyle: song.preset.style,
                              customStyle: '',
                              gender:
                                song.preset.vocal.type === 'MODEL'
                                  ? song.preset.vocal.gender
                                  : 'male',
                            });
                            setCurrentSong(song);
                          }}
                          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-all"
                        >
                          Прослушать
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await client.songs.delete(song.id);
                              await reloadUserData();
                              setCurrentPage((p) =>
                                Math.max(
                                  1,
                                  Math.min(
                                    p,
                                    Math.ceil(
                                      (songs.length - 1) / ITEMS_PER_PAGE,
                                    ),
                                  ),
                                ),
                              );
                            } catch (error) {
                              openError(translateApiError(error));
                            }
                          }}
                          className="px-6 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-medium transition-all"
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {songs.length > ITEMS_PER_PAGE && (
                  <div className="flex justify-center items-center gap-4 mt-8">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-medium hover:border-purple-400 hover:text-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Назад
                    </button>

                    <div className="flex gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-xl font-medium transition-all ${
                              currentPage === page
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                                : 'bg-white border-2 border-gray-200 hover:border-purple-400 hover:text-purple-600'
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      )}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-medium hover:border-purple-400 hover:text-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Вперед
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Profile tab ── */}
        {activeTab === 'profile' && (
          <div className="w-full max-w-xl">
            {!profile.isLoggedIn || profile.isAnonymous ? (
              <div className="w-full rounded-3xl bg-white/80 p-6 shadow-xl backdrop-blur-md sm:p-8">
                <ProfileAuth
                  embedded
                  onAuthSuccess={async (token, name) => {
                    localStorage.setItem('token', token);
                    localStorage.removeItem('anonymous_token');
                    patchProfile({
                      isLoggedIn: true,
                      isAnonymous: false,
                      userName: name,
                    });
                    await reloadUserData();
                  }}
                />
                <div className="mt-8 border-t border-purple-100 pt-8">
                  <TariffCards
                    onPurchase={async (tariffId) => {
                      try {
                        await ensureAnonymousRecoveryBeforePayment(async () => {
                          const response =
                            await client.payments.purchaseGeneration({
                              tariffId,
                              context: { source: 'PROFILE' },
                            });
                          window.location.href = response.paymentUrl;
                        });
                      } catch (error) {
                        openPaymentError(translateApiError(error));
                      }
                    }}
                  />
                </div>
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <AnonymousRecovery
                    onSuccess={async (token) => {
                      localStorage.setItem('token', token);
                      localStorage.removeItem('anonymous_token');
                      await reloadUserData();
                    }}
                  />
                </div>
              </div>
            ) : (
              <ProfileInfo
                userName={profile.userName}
                savedSongsCount={profile.savedSongsCount}
                generationsBalance={profile.generationsBalance}
                vocalGenerationsBalance={profile.vocalGenerationsBalance}
                tracksBalance={profile.tracksBalance}
                onBuyTariff={async (tariffId) => {
                  try {
                    await ensureAnonymousRecoveryBeforePayment(async () => {
                      const response = await client.payments.purchaseGeneration(
                        {
                          tariffId,
                          context: { source: 'PROFILE' },
                        },
                      );
                      window.location.href = response.paymentUrl;
                    });
                  } catch (error) {
                    openPaymentError(translateApiError(error));
                  }
                }}
                onLogout={async () => {
                  try {
                    await client.account.logOut();
                  } catch {
                    /* ignore */
                  } finally {
                    localStorage.removeItem('token');
                    localStorage.removeItem('anonymous_token');
                    localStorage.removeItem(ACTIVE_GENERATION_KEY);
                    window.location.reload();
                  }
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <footer className="mt-auto border-t border-gray-200 bg-white/80 py-3 backdrop-blur-sm sm:py-6">
        <div className="container mx-auto px-4">
          <nav
            aria-label="Юридическая информация"
            className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-8"
          >
            {FOOTER_LEGAL_KEYS.map((key, i) => (
              <Fragment key={key}>
                <button
                  type="button"
                  onClick={() => setActiveModal(key)}
                  className="text-xs font-medium tracking-[-0.31px] text-[#4a5565] transition-colors hover:text-purple-600 sm:text-base sm:leading-6"
                >
                  {LEGAL_MODAL_TITLES[key]}
                </button>
                {i < FOOTER_LEGAL_KEYS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden text-xs text-[#99a1af] sm:inline"
                  >
                    •
                  </span>
                )}
              </Fragment>
            ))}
          </nav>
          <div className="mt-2 flex flex-col items-center sm:mt-4">
            <button
              type="button"
              title="Идентификационный номер налогоплательщика"
              onClick={() => setActiveModal('requisites')}
              className="py-1 text-xs font-medium text-[#99a1af] transition-colors hover:text-purple-600"
            >
              Email: sup_musiccreator@mail.ru
            </button>
            <p className="text-xs text-[#6a7282]">
              © 2026 Генератор песен. Все права защищены.
            </p>
          </div>
        </div>
      </footer>

      {/* ── Modals ── */}
      <AnimatePresence>
        {activeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            {LEGAL_MODALS.has(activeModal) && (
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              >
                <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {LEGAL_MODAL_TITLES[activeModal]}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-white rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
                <div className="overflow-y-auto px-8 py-6">
                  <LegalModalContent modal={activeModal} />
                </div>
                <div className="px-8 py-4 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={closeModal}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                  >
                    Закрыть
                  </button>
                </div>
              </motion.div>
            )}

            {activeModal === 'error' && (
              <InfoModal
                title="Что-то пошло не так"
                message={
                  errorMessage || 'Попробуйте повторить действие чуть позже.'
                }
                iconClassName="bg-red-100"
                icon={<X className="w-10 h-10 text-red-600" />}
                onClose={closeModal}
              />
            )}

            {activeModal === 'generationFailed' && (
              <InfoModal
                title="Что-то пошло не так"
                message="Не удалось сгенерировать песню. Баланс генераций был возвращён — попробуйте создать песню ещё раз."
                iconClassName="bg-red-100"
                icon={<X className="w-10 h-10 text-red-600" />}
                onClose={closeModal}
              />
            )}

            {activeModal === 'paymentSuccess' && (
              <InfoModal
                title="Оплата прошла успешно"
                message="Ваши данные будут обновлены после подтверждения платежа."
                iconClassName="bg-green-100"
                icon={
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                }
                onClose={async () => {
                  closeModal();
                  await reloadUserData().catch(() => undefined);
                }}
              />
            )}

            {activeModal === 'paymentError' && (
              <InfoModal
                title="Оплата не прошла"
                message={errorMessage || 'Попробуйте ещё раз.'}
                iconClassName="bg-red-100"
                icon={<X className="w-10 h-10 text-red-600" />}
                onClose={closeModal}
              />
            )}

            {activeModal === 'anonymousRecovery' && anonymousRecoveryCode && (
              <AnonymousRecoveryModal
                code={anonymousRecoveryCode}
                onContinue={continueAfterAnonymousRecovery}
                onClose={closeAnonymousRecovery}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
